/**
 * Suite images keyed by `{themeId}_{colorwayId}`. When a matching image exists
 * the PDP gallery shows a flat-lay of the full suite (invitation, save the
 * date, enclosure cards) for that specific design + color combination.
 *
 * To add a new suite image:
 * 1. Upload the image to S3 under `suite-preview/suite_preview-{key}.jpg`
 * 2. Add the key to this set
 */
export const SUITE_IMAGE_KEYS: ReadonlySet<string> = new Set([
  'bohemian_eucalyptus',
  'bonsoir_black',
  'bonsoir_blue',
  'bonsoir_green',
  'bonsoir_orange',
  'bonsoir_pink',
  'bonsoir_red',
  'emilio_blush',
  'emilio_brick',
  'emilio_mocha',
  'emilio_mustard',
  'emilio_navy',
  'emilio_sage',
  'emilio_white',
  'felicity_black',
  'felicity_cream',
  'felicity_garnet',
  'felicity_lavender',
  'felicity_navy',
  'felicity_olive',
  'felicity_terra',
  'felicity_white',
  'gala_black',
  'gala_blue',
  'gala_magenta',
  'gala_olive',
  'gala_tomato',
  'ivory_garden',
  'la_luce_black',
  'la_luce_blue',
  'la_luce_green',
  'la_luce_red',
  'love_and_leaves',
  'minimalist_arch_black',
  'minimalist_arch_blue',
  'minimalist_arch_blush',
  'minimalist_arch_cream',
  'minimalist_arch_mocha',
  'minimalist_arch_olive',
  'minimalist_arch_shell',
  'minimalist_arch_terra',
  'minimalist_arch_white',
  'quill_blue',
  'quill_burgundy',
  'quill_chartreuse',
  'quill_cherry',
  'quill_denim',
  'quill_green',
  'quill_red',
  'quill_white',
  'tiffany_black',
  'tiffany_cobalt',
  'tiffany_lilac',
  'tiffany_red',
  'tiffany_sage',
  'tiffany_sienna',
  'tiffany_white',
]);

/**
 * Builds the suite image lookup key from a themeId and optional colorwayId,
 * using the same `{themeId}_{colorwayId}` convention as the theme-search
 * embeddings index.
 */
export const suiteImageKey = (themeId: string, colorwayId: string | undefined): string => {
  if (!colorwayId) return themeId;
  return `${themeId}_${colorwayId.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
};

// The `suite-preview/suite_preview-` path segments are the existing S3 upload
// convention, so they stay as-is even though the code now says "suite image".
export const getSuiteImageUrl = (key: string): string => `https://withjoy.com/assets/public/suite-preview/suite_preview-${key}.jpg`;

/**
 * Returns whether a suite image exists for a given themeId and colorwayId.
 * Used to enrich telemetry events so we can measure the impact of suite images
 * on conversion, independent of whether the feature flag is on.
 */
export const hasSuiteImage = (themeId: string, colorwayId?: string): boolean => {
  const key = suiteImageKey(themeId, colorwayId);
  return SUITE_IMAGE_KEYS.has(key);
};
